import { FieldErrors, FieldValues } from 'react-hook-form';

/**
 * Resolves a dot-path field name against react-hook-form's nested errors object.
 * Returns the error object if found, undefined otherwise.
 *
 * Example: getFieldError(errors, 'gender.value') → errors.gender?.value
 */
export const getFieldError = (
  errors: FieldErrors<FieldValues>,
  name: string
): { message?: string } | undefined => {
  return name
    .split('.')
    .reduce<
      Record<string, unknown> | undefined
    >((obj, key) => obj?.[key] as Record<string, unknown> | undefined, errors as Record<string, unknown>) as
    | { message?: string }
    | undefined;
};

// An error object is only useful to a control if it actually carries text. A
// parent whose *child* holds the message (e.g. `{ label: { message } }`) is
// truthy but renders nothing, which is the exact failure mode below.
const withMessage = (error: { message?: string } | undefined): { message?: string } | undefined =>
  error?.message ? error : undefined;

/**
 * Resolves the error for an option-shaped control (a `{ value, label }` field).
 *
 * Such a field can be given its error at any of three paths, and which one
 * depends on how the schema was written rather than on anything the control
 * knows:
 *
 *   - `<name>.value` — schemas validating the inner value, e.g.
 *     `optionSchemaRequired`, or a `.refine()` with an explicit `path`.
 *   - `<name>.label` — schemas validating the display string instead, e.g.
 *     `facilitySchema` and `facilitySchemaRequiredNoAddress`, both of which
 *     put "Facility Name is required" on `label`.
 *   - `<name>` — object-level `.refine()`s, and server warnings from
 *     `useServerWarnings`, which set errors at the top-level key.
 *
 * [MCV-1522] Checking only one path silently swallows the others, which is how
 * required Selects came to block submit while rendering no message at all.
 * Every option-shaped control resolves through here so the sources can't
 * diverge again.
 *
 * Precedence is value → label → field, and a path only wins if it carries a
 * message; otherwise the search continues. The final fallback returns whatever
 * sits at the field path even when message-less, so a control that previously
 * showed an error *border* from a bare truthy error keeps doing so.
 */
export const getOptionFieldError = (
  errors: FieldErrors<FieldValues>,
  name: string
): { message?: string } | undefined =>
  withMessage(getFieldError(errors, `${name}.value`)) ??
  withMessage(getFieldError(errors, `${name}.label`)) ??
  withMessage(getFieldError(errors, name)) ??
  getFieldError(errors, name);

/**
 * Matches an option label against typed text one token at a time.
 *
 * [MCV-1626] react-select's stock filter (and the hand-rolled
 * `label.includes(inputValue)` this replaced) requires the typed text to appear
 * in the label as one contiguous run, so word order and punctuation both have
 * to match: "toledo university" hides "University of Toledo", and
 * "university of toledo health" hides
 * "University of Toledo - Health Science Campus" purely because of the hyphen.
 * Splitting the input on non-alphanumerics and requiring each token separately
 * makes both irrelevant, and mirrors the tokenized filter the institutions
 * search endpoint now applies server-side -- without this the client would
 * throw away rows the server had just matched.
 */
export const matchesAllTokens = (label: string, inputValue: string): boolean => {
  const haystack = label.toLowerCase();
  return inputValue
    .toLowerCase()
    .split(/[^0-9a-z]+/)
    .filter(Boolean)
    .every((token) => haystack.includes(token));
};
